import { FC, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom'

import s from './MyAdSpots.module.scss'

import AdSpotCard from './components/AdSpotCard'

import { METAVERSE_IMAGE_BY_NAME_FILTER } from 'constants/MetaverseIcons'
import { SORT_ICON_BY_VALUE, SORT_OPTIONS } from 'constants/sortOptions'

import { useCreateMetaOpts } from 'hooks/useCreateMetaOpts'
import { usePagination } from 'hooks/usePagination'

import { useAppSuccessMessageContext } from 'contexts/SuccessMessageContext'

import { adSpotsApi } from 'api/adSpots-api'

import { GlobeIcon } from 'assets/icons/GlobeIcon'
import { SelectIcon } from 'assets/icons/SelectIcon'
import publisher from 'assets/svg/icons-publisher.svg'

import { SelectControlled } from 'components/Controls/Select/SelectControlled'
import { CustomButton } from 'components/UI/CustomButton/CustomButton'
import Header from 'components/UI/Header'
import { NoContent } from 'components/UI/NoContent/NoContent'
import PageTitle from 'components/UI/PageTitle'
import Search from 'components/UI/Search'
import { BurgerWrapper } from 'components/UI/BurgerWrapper/BurgerWrapper'

const MyAdSpots: FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { setSuccessMessage } = useAppSuccessMessageContext()
  const { filterMetaverseOptions } = useCreateMetaOpts()

  const { control, setValue } = useForm({
    mode: 'onBlur',
    defaultValues: {
      sortBy: SORT_OPTIONS[0],
      filterBy: filterMetaverseOptions[0],
    },
  })

  const {
    data: adSpots,
    loading,
    totalElements,
    paging,
    setPaging,
    forceUpdate,
    handleFilterChange,
    handleDelete,
    deleteLoading,
  } = usePagination({
    getData: adSpotsApi.paging,
    formValueSetter: setValue,
    deleteData: adSpotsApi.delete,
  })

  //sets select value if query not empty on page open
  const sortBy = searchParams.get('d__sort') || ''
  useEffect(() => {
    if (sortBy)
      setValue(
        'sortBy', //@ts-ignore
        SORT_OPTIONS.find((opt) => opt.value === sortBy)
      )
  }, [])

  const fMetaverse = searchParams.get('f__spot_type') || ''
  useEffect(() => {
    if (fMetaverse) setValue('filterBy', { value: fMetaverse, label: fMetaverse.split('-')[0] })
  }, [])

  //after creation of adspot
  const adSpotId = localStorage.getItem('adSpotId') || 0
  const pin = localStorage.getItem('pin') || ''
  const adSpotCreated = localStorage.getItem('adSpotCreated') || ''

  useEffect(() => {
    const updateAdSpot = () => {
      if (pin) {
        navigate(`created/${pin}?${searchParams}`, { replace: true })
      }
      localStorage.removeItem('adSpotId')
      localStorage.removeItem('pin')
      localStorage.removeItem('adSpotCreated')
      setPaging((prev) => ({ ...prev, page: { ...prev.page, page_number: 0 } }))
      forceUpdate()
    }
    if (adSpotId && adSpotCreated) {
      updateAdSpot()
    }
  }, [adSpotId, pin])

  //refresh after edit
  const isAfterEdit = localStorage.getItem('adSpaceEdit') || ''
  useEffect(() => {
    if (isAfterEdit) {
      setPaging((prev) => ({ ...prev, page: { ...prev.page, page_number: 0 } }))
      forceUpdate()
      localStorage.removeItem('adSpaceEdit')
      setSuccessMessage('The Ad Space successfully edited.')
    }
  }, [isAfterEdit])

  const title =
    paging?.quick_search?.length || paging?.filters?.length
      ? adSpots?.length
        ? 'Found Ad Spaces'
        : 'Nothing found'
      : adSpots?.length
      ? 'My Ad Spaces'
      : null

  return (
    <>
      <Outlet />
      <Header>
        <div className={s.headerWrapper}>
          <CustomButton variant="primary" onClick={() => navigate(`create?${searchParams}`)} size="240" className={s.button}>
            Add Ad Space
          </CustomButton>
          <BurgerWrapper>
            <SelectControlled
              name="sortBy"
              control={control}
              icon={<SelectIcon />}
              options={SORT_OPTIONS}
              optionsIcons={SORT_ICON_BY_VALUE}
              className={s.sortSelect}
              placeholder="Sort"
              onChange={(opt) =>
                handleFilterChange({
                  fieldName: 'sortBy',
                  searchParamName: 'd__sort',
                  option: opt,
                })
              }
            />
            <SelectControlled
              control={control}
              name="filterBy"
              icon={<GlobeIcon />}
              options={filterMetaverseOptions}
              className={s.metaSelect}
              placeholder="Metaverse"
              optionsIcons={METAVERSE_IMAGE_BY_NAME_FILTER}
              onChange={(opt) =>
                handleFilterChange({
                  fieldName: 'filterBy',
                  searchParamName: 'f__spot_type',
                  option: opt,
                })
              }
            />
            <Search paramPrefix="adSpot" className={s.search} />
          </BurgerWrapper>
        </div>
        <PageTitle title={title} count={totalElements} loading={loading || deleteLoading} />
      </Header>
      <main>
      {adSpots?.length ? (
        <div className={s.adSpotsContainer}>
          {adSpots.map((adSpot) => (
            <AdSpotCard deleteAdSpot={handleDelete} adSpot={adSpot} key={adSpot.id} />
          ))}
        </div>
      ) : !paging?.quick_search?.length && !paging?.filters?.length && !loading ? (
        <NoContent icon={publisher} title="No Ad Spaces" subtitle="You haven’t added any ad spaces yet." />
      ) : null}
      </main>
    </>
  )
}

export default MyAdSpots
